import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from './LoadingSpinner';

const SettingsNew = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [userSettings, setUserSettings] = useState({
    // Default target values
    defaultCapRate: '',
    defaultCoCReturn: '',
    defaultTotalROI: '',
    // Default expense rates for long-term rentals
    defaultManagementFee: '',
    defaultVacancyRate: '',
    defaultMaintenanceRate: '',
    // Default expense rates for short-term rentals
    defaultShortTermManagementFee: '',
    defaultOccupancyRate: '',
    defaultShortTermMaintenanceRate: ''
  });
  const [originalSettings, setOriginalSettings] = useState({});
  
  // Fetch user settings on component mount
  useEffect(() => {
    fetchUserSettings();
  }, [currentUser]);

  const fetchUserSettings = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setIsLoading(true);
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      // Start with empty settings
      const settings = {
        defaultCapRate: '',
        defaultCoCReturn: '',
        defaultTotalROI: '',
        defaultManagementFee: '',
        defaultVacancyRate: '',
        defaultMaintenanceRate: '',
        defaultShortTermManagementFee: '',
        defaultOccupancyRate: '',
        defaultShortTermMaintenanceRate: ''
      };
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        console.log('User data from Firestore:', userData);
        
        // Only set values that exist in the user data
        if ('defaultCapRate' in userData) settings.defaultCapRate = userData.defaultCapRate;
        if ('defaultCoCReturn' in userData) settings.defaultCoCReturn = userData.defaultCoCReturn;
        if ('defaultTotalROI' in userData) settings.defaultTotalROI = userData.defaultTotalROI;
        if ('defaultManagementFee' in userData) settings.defaultManagementFee = userData.defaultManagementFee;
        if ('defaultVacancyRate' in userData) settings.defaultVacancyRate = userData.defaultVacancyRate;
        if ('defaultMaintenanceRate' in userData) settings.defaultMaintenanceRate = userData.defaultMaintenanceRate;
        if ('defaultShortTermManagementFee' in userData) settings.defaultShortTermManagementFee = userData.defaultShortTermManagementFee;
        if ('defaultOccupancyRate' in userData) settings.defaultOccupancyRate = userData.defaultOccupancyRate;
        if ('defaultShortTermMaintenanceRate' in userData) settings.defaultShortTermMaintenanceRate = userData.defaultShortTermMaintenanceRate;
      }
      
      console.log('Settings after processing:', settings);
      setUserSettings(settings);
      setOriginalSettings({...settings});
    } catch (error) {
      console.error('Error fetching user settings:', error);
      showError('Failed to load settings: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setIsLoading(true);
      const userDocRef = doc(db, 'users', currentUser.uid);
      
      // Build the update object
      const updateData = {};
      
      // For each setting, determine if it should be updated
      Object.keys(userSettings).forEach(key => {
        // If the value has changed from original, update it
        if (userSettings[key] !== originalSettings[key]) {
          updateData[key] = userSettings[key];
        }
      });
      
      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        updateData.updatedAt = new Date();
        
        console.log('Updating with data:', updateData);
        
        // Check if document exists
        const docSnap = await getDoc(userDocRef);
        
        if (docSnap.exists()) {
          await updateDoc(userDocRef, updateData);
        } else {
          await setDoc(userDocRef, {
            ...updateData,
            uid: currentUser.uid,
            email: currentUser.email,
            createdAt: new Date()
          });
        }
        
        // Update original settings to reflect new values
        setOriginalSettings({...userSettings});
        showSuccess('Settings saved successfully!');
      } else {
        console.log('No changes to save');
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving user settings:', error);
      showError('Failed to save settings: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    // Allow empty string or valid numbers
    if (value === '' || (!isNaN(parseFloat(value)) && isFinite(value))) {
      setUserSettings(prev => ({
        ...prev,
        [id]: value
      }));
    }
  };
  
  const handleCancel = () => {
    setUserSettings({...originalSettings});
    setIsEditing(false);
  };

  return (
    <div className="container py-4">
      {isLoading && <LoadingSpinner />}
      
      {/* Header */}
      <div className="row mb-3">
        <div className="col">
          <h2 className="mb-0">Settings</h2>
          <p className="text-muted">Manage your default calculation settings.</p>
        </div>
        <div className="col-auto">
          {!isEditing ? (
            <button 
              className="btn btn-outline-light" 
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
              style={{ transition: 'all 0.3s ease' }}
              onMouseOver={(e) => e.currentTarget.classList.replace('btn-outline-light', 'btn-primary')}
              onMouseOut={(e) => e.currentTarget.classList.replace('btn-primary', 'btn-outline-light')}
            >
              <i className="bi bi-pencil me-1"></i> Edit Settings
            </button>
          ) : (
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-secondary" 
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>Save Changes</>  
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Target Values Card */}
      <div className="card bg-dark text-white border-secondary mb-4">
        <div className="card-header border-0">
          <h5 className="mb-0">Target Values</h5>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">Applied automatically when adding new properties</p>
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Cap Rate Target</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultCapRate" 
                        placeholder="Enter default cap rate percentage"
                        value={userSettings.defaultCapRate}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Cap Rate Target</div>
                    <div className="text-white">{userSettings.defaultCapRate ? `${userSettings.defaultCapRate}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Cash on Cash Return</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultCoCReturn" 
                        placeholder="Enter default CoC return percentage"
                        value={userSettings.defaultCoCReturn}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Cash on Cash Return</div>
                    <div className="text-white">{userSettings.defaultCoCReturn ? `${userSettings.defaultCoCReturn}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Total ROI</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultTotalROI" 
                        placeholder="Enter default total ROI percentage"
                        value={userSettings.defaultTotalROI}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Total ROI Target</div>
                    <div className="text-white">{userSettings.defaultTotalROI ? `${userSettings.defaultTotalROI}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Long-Term Rental Card */}
      <div className="card bg-dark text-white border-secondary mb-4">
        <div className="card-header border-0">
          <h5 className="mb-0">Long-Term Rental Expenses</h5>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">Used for long-term rental scenarios</p>
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Management Fee</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultManagementFee" 
                        placeholder="Enter default management fee percentage"
                        value={userSettings.defaultManagementFee}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Management Fee</div>
                    <div className="text-white">{userSettings.defaultManagementFee ? `${userSettings.defaultManagementFee}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Vacancy Rate</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultVacancyRate" 
                        placeholder="Enter default vacancy rate percentage"
                        value={userSettings.defaultVacancyRate}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Vacancy Rate</div>
                    <div className="text-white">{userSettings.defaultVacancyRate ? `${userSettings.defaultVacancyRate}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Maintenance Rate</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultMaintenanceRate" 
                        placeholder="Enter default maintenance rate percentage"
                        value={userSettings.defaultMaintenanceRate}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Maintenance Rate</div>
                    <div className="text-white">{userSettings.defaultMaintenanceRate ? `${userSettings.defaultMaintenanceRate}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Short-Term Rental Card */}
      <div className="card bg-dark text-white border-secondary mb-4">
        <div className="card-header border-0">
          <h5 className="mb-0">Short-Term Rental Expenses</h5>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">Used for short-term rental scenarios</p>
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Management Fee</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultShortTermManagementFee" 
                        placeholder="Enter default management fee percentage"
                        value={userSettings.defaultShortTermManagementFee}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Management Fee</div>
                    <div className="text-white">{userSettings.defaultShortTermManagementFee ? `${userSettings.defaultShortTermManagementFee}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Occupancy Rate</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultOccupancyRate" 
                        placeholder="Enter default occupancy rate percentage"
                        value={userSettings.defaultOccupancyRate}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Occupancy Rate</div>
                    <div className="text-white">{userSettings.defaultOccupancyRate ? `${userSettings.defaultOccupancyRate}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="mb-3">
                {isEditing ? (
                  <div>
                    <div className="text-muted small mb-1">Maintenance Rate</div>
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-dark text-light border-secondary" 
                        id="defaultShortTermMaintenanceRate" 
                        placeholder="Enter default maintenance rate percentage"
                        value={userSettings.defaultShortTermMaintenanceRate}
                        onChange={handleInputChange}
                      />
                      <span className="input-group-text bg-dark text-white border-secondary">%</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted small mb-1">Maintenance Rate</div>
                    <div className="text-white">{userSettings.defaultShortTermMaintenanceRate ? `${userSettings.defaultShortTermMaintenanceRate}%` : '-'}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsNew;
